import { useEffect } from 'react';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { TooltipProvider } from '@glob/ui';
import { useSync } from '../../lib/sync/useSync';
import { OfflineBanner } from '../../components/OfflineBanner';
import { Topbar } from '../../components/shell/Topbar';
import { Rail } from '../../components/shell/Rail';
import { CommandBar } from '../../components/shell/CommandBar';
import { GlobCompanion } from '../../features/glob/GlobCompanion';
import { startIdleTimer, stopIdleTimer } from '../../lib/idle-timer';
import { useVaultStore } from '../../features/auth/vaultStore';

function AuthedLayout() {
  useSync();
  // Mount the idle timer at the highest authed-only mount point so it covers
  // every /app/* and /app/settings/* route uniformly. The unlock/register
  // routes (which live under _public) do not run the timer — there is no
  // unlocked vault to lock there.
  useEffect(() => {
    startIdleTimer();
    return () => stopIdleTimer();
  }, []);
  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-col h-screen bg-bg-0 text-text-1">
        <OfflineBanner />
        <Topbar />
        <div className="flex flex-1 min-h-0">
          <Rail />
          <main className="flex-1 min-w-0 overflow-auto">
            <Outlet />
          </main>
        </div>
        <CommandBar />
        <GlobCompanion />
      </div>
    </TooltipProvider>
  );
}

export const Route = createFileRoute('/_authed')({
  // The vault lives in tab memory only (never persisted), so a reload — or
  // landing here before unlocking — leaves the store locked. Bounce to /unlock
  // instead of rendering a dead, empty shell. Registration calls setUnlocked
  // before navigating here, so a fresh sign-up passes straight through.
  beforeLoad: () => {
    if (useVaultStore.getState().status !== 'unlocked') {
      // TanStack Router's redirect() is a control-flow signal meant to be
      // thrown; it isn't an Error subclass.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/unlock' });
    }
  },
  component: AuthedLayout,
});
